import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import kivoIcon from '../assets/logo/kivo-icon-only.png';

export const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-[#FAFAF8]">
      {/* Header */}
      <div className="max-w-3xl mx-auto px-6 pt-10 pb-6">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-700 mb-6">
          <ArrowLeft className="w-4 h-4" />
          Back to Kivo
        </Link>
        <div className="flex items-center gap-2 mb-8">
          <img src={kivoIcon} alt="Kivo" className="h-8 w-auto" />
          <span className="text-lg font-bold text-slate-800 font-heading">Kivo</span>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-6 pb-20">
        <h1 className="text-3xl font-bold text-slate-900 font-heading mb-2">Privacy Policy</h1>
        <p className="text-sm text-slate-400 mb-10">Last updated: March 23, 2026</p>

        <div className="prose prose-slate prose-sm max-w-none space-y-8">
          <section>
            <h2 className="text-lg font-semibold text-slate-900 mb-3">1. Who we are</h2>
            <p className="text-slate-600 leading-relaxed">
              Kivo ("we", "us", "our") is a caregiver coordination service for families with school-age children.
              We help parents stay informed about their child's day through automated school email monitoring,
              caregiver WhatsApp check-ins, and structured daily summaries.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-slate-900 mb-3">2. Information we collect</h2>
            <p className="text-slate-600 leading-relaxed mb-3">We collect the following categories of personal data:</p>
            <ul className="list-disc pl-5 space-y-2 text-slate-600">
              <li><strong>Account information:</strong> Parent name, email address, password (hashed), phone number.</li>
              <li><strong>Family information:</strong> Child name, age, school, grade, transport mode, routines, dietary preferences, interests.</li>
              <li><strong>Caregiver information:</strong> Name, WhatsApp phone number, preferred language.</li>
              <li><strong>School emails:</strong> Email content from your connected school inbox, used to extract tasks, events, and alerts.</li>
              <li><strong>WhatsApp messages:</strong> Messages exchanged between the caregiver and the Kivo system via WhatsApp.</li>
              <li><strong>Usage data:</strong> Login timestamps, feature usage for service improvement.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-slate-900 mb-3">3. How we use your information</h2>
            <ul className="list-disc pl-5 space-y-2 text-slate-600">
              <li>Deliver the core service: school email monitoring, caregiver check-ins, daily summaries.</li>
              <li>Extract tasks, deadlines, and alerts from school communications.</li>
              <li>Send WhatsApp messages to caregivers and parents on your behalf.</li>
              <li>Generate AI-powered summaries of your child's day.</li>
              <li>Improve service quality and reliability.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-slate-900 mb-3">4. Third-party services</h2>
            <p className="text-slate-600 leading-relaxed mb-3">We use the following third-party services to operate Kivo:</p>
            <ul className="list-disc pl-5 space-y-2 text-slate-600">
              <li><strong>Google Gmail API:</strong> To read school emails you have authorized. We only access emails from school senders you configure. We do not read personal or unrelated emails.</li>
              <li><strong>Twilio (WhatsApp):</strong> To send and receive WhatsApp messages with caregivers and parents.</li>
              <li><strong>OpenAI:</strong> To generate AI summaries and extract tasks from school emails. Email content is sent to OpenAI's API for processing. OpenAI does not use this data for training.</li>
              <li><strong>Supabase:</strong> For secure database hosting (PostgreSQL).</li>
              <li><strong>Vercel / Railway:</strong> For application hosting.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-slate-900 mb-3">5. Data about children</h2>
            <p className="text-slate-600 leading-relaxed">
              We collect child information (name, age, school, interests) solely to provide the coordination
              service to parents. This data is controlled entirely by the parent and can be updated or deleted
              at any time. We do not sell, share, or use child data for advertising or profiling purposes.
              All child data processing is done with verifiable parental consent as required by Indian law.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-slate-900 mb-3">6. Data security</h2>
            <ul className="list-disc pl-5 space-y-2 text-slate-600">
              <li>All data is transmitted over HTTPS.</li>
              <li>Passwords are hashed using bcrypt.</li>
              <li>Gmail OAuth tokens are encrypted at rest using Fernet symmetric encryption.</li>
              <li>Access to data is restricted by JWT-based authentication and role-based authorization.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-slate-900 mb-3">7. Data retention</h2>
            <p className="text-slate-600 leading-relaxed">
              We retain your data for as long as your account is active. School email content is stored for
              task extraction and historical reference. You may request deletion of your account and all
              associated data at any time by contacting us.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-slate-900 mb-3">8. Your rights under DPDP Act, 2023</h2>
            <p className="text-slate-600 leading-relaxed mb-3">
              Under India's Digital Personal Data Protection Act, 2023, you have the right to:
            </p>
            <ul className="list-disc pl-5 space-y-2 text-slate-600">
              <li>Access a summary of your personal data and processing activities.</li>
              <li>Request correction of inaccurate data.</li>
              <li>Request erasure of your personal data.</li>
              <li>Withdraw consent for data processing.</li>
              <li>Nominate another person to exercise your rights on your behalf.</li>
              <li>Lodge a grievance with us or with the Data Protection Board of India.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-slate-900 mb-3">9. Contact us</h2>
            <p className="text-slate-600 leading-relaxed">
              For privacy-related requests, data deletion, or grievances, please contact us at:{' '}
              <a href="mailto:privacy@kivo.family" className="text-primary hover:underline">privacy@kivo.family</a>
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
